import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import QuickSettingsModal from '../components/QuickSettingsModal';
import TagInput from '../components/TagInput';
import { getStatusOptions, getOwnershipOptions } from '../utils/statusOptions';
import { CONDITION_OPTIONS } from '../utils/filterEngine';
import '../styles/ManualAddMediaPage.css';

// Maps this page's own lowercase `mediaType` values to the backend MediaItem.type, same mapping
// used by AddMediaPage for getStatusOptions/getOwnershipOptions/QuickSettingsModal.
const BACKEND_TYPE_MAP = {
  anime: 'Anime',
  manga: 'Manga',
  vinyl: 'Music',
  cd: 'Music',
  steam: 'SteamGame',
  psp: 'PhysicalGame',
  vita: 'PhysicalGame',
  nds: 'PhysicalGame',
  switch: 'PhysicalGame',
  movie: 'ScreenMedia',
  tv: 'ScreenMedia',
  books: 'Book',
  comics: 'Book',
};

const mediaTypes = [
  { value: 'anime', label: 'Anime' },
  { value: 'manga', label: 'Manga' },
  { value: 'vinyl', label: 'Vinyl' },
  { value: 'cd', label: 'CD' },
  { value: 'steam', label: 'Steam Game' },
  { value: 'psp', label: 'PSP Game' },
  { value: 'vita', label: 'Vita Game' },
  { value: 'nds', label: 'NDS Game' },
  { value: 'switch', label: 'Switch Game' },
  { value: 'movie', label: 'Movies' },
  { value: 'tv', label: 'TV Series' },
  { value: 'books', label: 'Books' },
  { value: 'comics', label: 'Comics' },
];

// Types that use the Owned/Wishlisted/... ownership vocabulary + physical condition, matching
// QuickSettingsModal's OWNERSHIP_TYPES list.
const OWNERSHIP_TYPES = ['Manga', 'Book', 'PhysicalGame', 'Music'];

// Per-media-type extra fields, mirrored from the backend's MANUAL_ADD_FIELD_MAP whitelist in
// add_media_routes.py - keep these two in sync when adding/removing fields.
const FIELD_SCHEMA = {
  anime: [
    { key: 'title_en', label: 'English Title', type: 'text' },
    { key: 'title_ja', label: 'Japanese Title', type: 'text' },
    { key: 'studios', label: 'Studios', type: 'text', placeholder: 'Comma-separated' },
    { key: 'episode_count', label: 'Episode Count', type: 'number' },
    { key: 'media_type', label: 'Format', type: 'select', options: ['TV', 'Movie', 'OVA', 'Special', 'ONA', 'Music'] },
    { key: 'airing_status', label: 'Airing Status', type: 'select', options: ['finished_airing', 'currently_airing', 'not_yet_aired'] },
    { key: 'rating', label: 'Age Rating', type: 'text', placeholder: 'e.g. PG-13' },
  ],
  manga: [
    { key: 'title_en', label: 'English Title', type: 'text' },
    { key: 'title_ja', label: 'Japanese Title', type: 'text' },
    { key: 'authors', label: 'Authors', type: 'text', placeholder: 'Comma-separated' },
    { key: 'volume_count', label: 'Volume Count', type: 'number' },
    { key: 'chapter_count', label: 'Chapter Count', type: 'number' },
    { key: 'publish_status', label: 'Publish Status', type: 'select', options: ['finished', 'currently_publishing', 'not_yet_published', 'on_hiatus', 'discontinued'] },
    { key: 'ownership_status', label: 'Ownership', type: 'ownership' },
    { key: 'condition', label: 'Condition', type: 'condition' },
  ],
  books: [
    { key: 'authors', label: 'Authors', type: 'text', placeholder: 'Comma-separated' },
    { key: 'isbn', label: 'ISBN', type: 'text' },
    { key: 'first_publish_year', label: 'First Publish Year', type: 'number' },
    { key: 'page_count', label: 'Page Count', type: 'number' },
    { key: 'ownership_status', label: 'Ownership', type: 'ownership' },
    { key: 'condition', label: 'Condition', type: 'condition' },
  ],
  comics: [
    { key: 'authors', label: 'Authors', type: 'text', placeholder: 'Comma-separated' },
    { key: 'isbn', label: 'ISBN', type: 'text' },
    { key: 'first_publish_year', label: 'First Publish Year', type: 'number' },
    { key: 'page_count', label: 'Page Count', type: 'number' },
    { key: 'ownership_status', label: 'Ownership', type: 'ownership' },
    { key: 'condition', label: 'Condition', type: 'condition' },
  ],
  vinyl: [
    { key: 'artist', label: 'Artist', type: 'text' },
    { key: 'album', label: 'Album', type: 'text', placeholder: 'Defaults to title' },
    { key: 'label', label: 'Label', type: 'text' },
    { key: 'year', label: 'Year', type: 'text' },
    { key: 'ownership_status', label: 'Ownership', type: 'ownership' },
    { key: 'condition', label: 'Condition', type: 'condition' },
  ],
  cd: [
    { key: 'artist', label: 'Artist', type: 'text' },
    { key: 'album', label: 'Album', type: 'text', placeholder: 'Defaults to title' },
    { key: 'label', label: 'Label', type: 'text' },
    { key: 'year', label: 'Year', type: 'text' },
    { key: 'ownership_status', label: 'Ownership', type: 'ownership' },
    { key: 'condition', label: 'Condition', type: 'condition' },
  ],
  steam: [
    { key: 'developer', label: 'Developer', type: 'text' },
    { key: 'publisher', label: 'Publisher', type: 'text' },
    { key: 'release_date', label: 'Release Date', type: 'text' },
  ],
  psp: [
    { key: 'developer', label: 'Developer', type: 'text' },
    { key: 'publisher', label: 'Publisher', type: 'text' },
    { key: 'release_date', label: 'Release Date', type: 'text' },
    { key: 'ownership_status', label: 'Ownership', type: 'ownership' },
    { key: 'condition', label: 'Condition', type: 'condition' },
  ],
  vita: [
    { key: 'developer', label: 'Developer', type: 'text' },
    { key: 'publisher', label: 'Publisher', type: 'text' },
    { key: 'release_date', label: 'Release Date', type: 'text' },
    { key: 'ownership_status', label: 'Ownership', type: 'ownership' },
    { key: 'condition', label: 'Condition', type: 'condition' },
  ],
  nds: [
    { key: 'developer', label: 'Developer', type: 'text' },
    { key: 'publisher', label: 'Publisher', type: 'text' },
    { key: 'release_date', label: 'Release Date', type: 'text' },
    { key: 'ownership_status', label: 'Ownership', type: 'ownership' },
    { key: 'condition', label: 'Condition', type: 'condition' },
  ],
  switch: [
    { key: 'developer', label: 'Developer', type: 'text' },
    { key: 'publisher', label: 'Publisher', type: 'text' },
    { key: 'release_date', label: 'Release Date', type: 'text' },
    { key: 'ownership_status', label: 'Ownership', type: 'ownership' },
    { key: 'condition', label: 'Condition', type: 'condition' },
  ],
  movie: [
    { key: 'original_title', label: 'Original Title', type: 'text' },
    { key: 'cast', label: 'Cast', type: 'text', placeholder: 'Comma-separated' },
    { key: 'rating', label: 'Age Rating', type: 'text' },
    { key: 'release_date', label: 'Release Date', type: 'text' },
  ],
  tv: [
    { key: 'original_title', label: 'Original Title', type: 'text' },
    { key: 'cast', label: 'Cast', type: 'text', placeholder: 'Comma-separated' },
    { key: 'creators', label: 'Creators', type: 'text', placeholder: 'Comma-separated' },
    { key: 'rating', label: 'Age Rating', type: 'text' },
    { key: 'air_date', label: 'Air Date', type: 'text' },
    { key: 'episode_count', label: 'Episode Count', type: 'number' },
  ],
};

const ManualAddMediaPage = ({ apiBaseUrl }) => {
  const { isAuthenticated, userRoles } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const initialType = mediaTypes.some((t) => t.value === location.state?.mediaType)
    ? location.state.mediaType
    : 'anime';

  const [mediaType, setMediaType] = useState(initialType);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState('');
  const [genreTags, setGenreTags] = useState([]);
  const [personalTags, setPersonalTags] = useState([]);
  const [extraFields, setExtraFields] = useState({});
  const [verticalFile, setVerticalFile] = useState(null);
  const [horizontalFile, setHorizontalFile] = useState(null);
  const [verticalPreview, setVerticalPreview] = useState(null);
  const [horizontalPreview, setHorizontalPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [quickSettings, setQuickSettings] = useState({ isOpen: false, mediaId: null, mediaType: null, title: '' });

  const backendType = BACKEND_TYPE_MAP[mediaType];
  const statusOptions = useMemo(() => getStatusOptions(backendType), [backendType]);
  const ownershipOptions = useMemo(() => getOwnershipOptions(backendType), [backendType]);
  const fields = FIELD_SCHEMA[mediaType] || [];

  // Extra fields are type-specific, so clear them (and re-default status) whenever the media
  // type changes rather than carrying over values that no longer apply.
  useEffect(() => {
    setExtraFields({});
    setStatus((statusOptions && statusOptions[statusOptions.length - 1]) || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaType]);

  useEffect(() => {
    return () => {
      if (verticalPreview) URL.revokeObjectURL(verticalPreview);
      if (horizontalPreview) URL.revokeObjectURL(horizontalPreview);
    };
  }, [verticalPreview, horizontalPreview]);

  if (!isAuthenticated || !userRoles.includes('manage-media')) {
    return (
      <div className="home-container">
        <h1 style={{ textAlign: 'center' }}>Add Media Manually</h1>
        <p style={{ textAlign: 'center' }}>You don't have permission to manually add media.</p>
      </div>
    );
  }

  const handleFileChange = (e, kind) => {
    const file = e.target.files?.[0] || null;
    if (kind === 'vertical') {
      setVerticalFile(file);
      setVerticalPreview(file ? URL.createObjectURL(file) : null);
    } else {
      setHorizontalFile(file);
      setHorizontalPreview(file ? URL.createObjectURL(file) : null);
    }
  };

  const setExtraField = (key, value) => {
    setExtraFields((prev) => ({ ...prev, [key]: value }));
  };

  const closeQuickSettings = () => {
    setQuickSettings((prev) => ({ ...prev, isOpen: false }));
    navigate(`/media-item/${quickSettings.mediaId}`);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) {
      setError('Title is required.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('media_type', mediaType);
      formData.append('title', title.trim());
      formData.append('description', description);
      formData.append('status', status);
      formData.append('tags', personalTags.join(', '));

      const extra = { ...extraFields };
      if (genreTags.length > 0) extra.genre = genreTags.join(', ');
      formData.append('extra', JSON.stringify(extra));

      if (verticalFile) formData.append('vertical_image', verticalFile);
      if (horizontalFile) formData.append('horizontal_image', horizontalFile);

      const res = await fetch(`${apiBaseUrl}/add/manual`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setQuickSettings({ isOpen: true, mediaId: data.media_id, mediaType: data.media_type, title: title.trim() });
      } else {
        setError(data.error || 'Failed to add media.');
      }
    } catch (err) {
      console.error(err);
      setError('Failed to add media.');
    } finally {
      setSubmitting(false);
    }
  };

  const renderExtraField = (field) => {
    const value = extraFields[field.key] ?? '';
    if (field.type === 'select') {
      return (
        <select className="select-field" value={value} onChange={(e) => setExtraField(field.key, e.target.value)}>
          <option value="">-- Select --</option>
          {field.options.map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      );
    }
    if (field.type === 'ownership') {
      return (
        <select className="select-field" value={value} onChange={(e) => setExtraField(field.key, e.target.value)}>
          <option value="">-- Select --</option>
          {ownershipOptions.map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      );
    }
    if (field.type === 'condition') {
      return (
        <select className="select-field" value={value} onChange={(e) => setExtraField(field.key, e.target.value)}>
          <option value="">-- Select --</option>
          {CONDITION_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      );
    }
    return (
      <input
        type={field.type === 'number' ? 'number' : 'text'}
        className="input-field"
        value={value}
        placeholder={field.placeholder || ''}
        onChange={(e) => setExtraField(field.key, e.target.value)}
      />
    );
  };

  return (
    <div className="home-container manual-add-page">
      <h1 style={{ textAlign: 'center', marginBottom: '20px' }}>Add Media Manually</h1>
      <p className="manual-add-subtitle">
        For media that doesn't exist yet or can't be found automatically. Fill in whatever you know - you can always edit it later.
      </p>

      <form className="manual-add-form" onSubmit={handleSubmit}>
        <div className="manual-add-section">
          <label className="manual-add-label">
            Media Type
            <select className="select-field" value={mediaType} onChange={(e) => setMediaType(e.target.value)}>
              {mediaTypes.map((type) => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </label>

          <label className="manual-add-label">
            Title *
            <input
              type="text"
              className="input-field"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Title"
              required
            />
          </label>

          <label className="manual-add-label">
            Status
            <select className="select-field" value={status} onChange={(e) => setStatus(e.target.value)}>
              {statusOptions.map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </label>

          <label className="manual-add-label manual-add-label-full">
            Description
            <textarea
              className="input-field manual-add-textarea"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Description / synopsis"
              rows={4}
            />
          </label>
        </div>

        <div className="manual-add-section">
          <label className="manual-add-label manual-add-label-full">
            Genre
            <TagInput apiBaseUrl={apiBaseUrl} selected={genreTags} onChange={setGenreTags} placeholder="Search or add a genre..." />
          </label>

          <label className="manual-add-label manual-add-label-full">
            Tags
            <TagInput apiBaseUrl={apiBaseUrl} selected={personalTags} onChange={setPersonalTags} placeholder="Search or add a tag..." />
          </label>
        </div>

        {fields.length > 0 && (
          <div className="manual-add-section manual-add-section-full">
            <h3 className="manual-add-section-title">{mediaTypes.find((t) => t.value === mediaType)?.label} Details</h3>
            <div className="manual-add-grid">
              {fields.map((field) => (
                <label key={field.key} className="manual-add-label">
                  {field.label}
                  {renderExtraField(field)}
                </label>
              ))}
            </div>
          </div>
        )}

        <div className="manual-add-section manual-add-section-full">
          <h3 className="manual-add-section-title">Images</h3>
          <div className="manual-add-image-row">
            <label className="manual-add-image-upload">
              Poster (vertical)
              {verticalPreview ? (
                <img src={verticalPreview} alt="Poster preview" className="manual-add-image-preview manual-add-image-preview-vertical" />
              ) : (
                <div className="manual-add-image-placeholder manual-add-image-placeholder-vertical">No image</div>
              )}
              <input type="file" accept="image/*" onChange={(e) => handleFileChange(e, 'vertical')} />
            </label>
            <label className="manual-add-image-upload">
              Banner (horizontal)
              {horizontalPreview ? (
                <img src={horizontalPreview} alt="Banner preview" className="manual-add-image-preview manual-add-image-preview-horizontal" />
              ) : (
                <div className="manual-add-image-placeholder manual-add-image-placeholder-horizontal">No image</div>
              )}
              <input type="file" accept="image/*" onChange={(e) => handleFileChange(e, 'horizontal')} />
            </label>
          </div>
        </div>

        {error && <p className="manual-add-error">{error}</p>}

        <div className="manual-add-actions">
          <button type="button" className="btn btn-secondary" onClick={() => navigate('/add-media')}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? 'Adding...' : 'Add Media'}
          </button>
        </div>
      </form>

      <QuickSettingsModal
        isOpen={quickSettings.isOpen}
        onClose={closeQuickSettings}
        mediaId={quickSettings.mediaId}
        mediaType={quickSettings.mediaType}
        apiBaseUrl={apiBaseUrl}
        title={quickSettings.title}
      />
    </div>
  );
};

export default ManualAddMediaPage;
